import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  Stack, 
  Menu, 
  MenuItem, 
  IconButton, 
  Button,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import { Logout, SwitchAccount, Warning, Person, InsightsOutlined, Psychology } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { selectTotalStudentCount } from '../../store/selectors/studentSelectors';
import { clearSelectedStudent, Student } from '../../store/slices/studentSlice';
import './style.css';

interface HeaderProps {
  selectedStudent: Student;
}

const Header: React.FC<HeaderProps> = ({ selectedStudent }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showAuthError, setShowAuthError] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get total student count from store
  const totalStudentCount = useSelector(selectTotalStudentCount);
  const hasMultipleStudents = totalStudentCount > 1;
  
  const open = Boolean(anchorEl);

  // Check for authentication errors on mount and periodically
  useEffect(() => {
    const checkAuthError = () => {
      const error = localStorage.getItem('auth_error');
      if (error) {
        setAuthErrorMessage(error);
        setShowAuthError(true);
        localStorage.removeItem('auth_error');
      }
    };

    // Check immediately
    checkAuthError();

    // Check periodically
    const interval = setInterval(checkAuthError, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchStudent = () => {
    handleClose();
    navigate('/select-student');
  };

  const goTo = (path: string) => () => {
    handleClose();
    navigate(path);
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearSelectedStudent());
    handleClose();
    setShowAuthError(false);
    // Force page reload to ensure complete state reset after logout
    window.location.href = '/login';
  };

  const handleAuthErrorClose = () => {
    setShowAuthError(false);
  };

  const handleAuthErrorLogout = () => {
    handleLogout();
  };

  return (
    <>
      <Box className="header-container">
        <img 
          src="/liveolympiad.svg" 
          alt="NextZenOlympiad" 
          className="header-logo"
          style={{ height: '64px', width: 'auto' }}
        />
        
        <Stack direction="row" spacing={2} alignItems="center">
          <Box className="header-user-info">
            <Typography variant="subtitle1" className="header-user-name">
              {selectedStudent.name.split(' ')[0]}
            </Typography>
            <Typography variant="body2" className="header-user-school">
              {selectedStudent.grade || 'Class-01'}
            </Typography>
          </Box>
          
          {/* Mobile-friendly logout button */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleLogout}
            sx={{
              display: { xs: 'flex', md: 'none' },
              minHeight: '36px',
              fontSize: '0.75rem',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'white',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            Logout
          </Button>
          
          <IconButton
            onClick={handleClick}
            size="small"
            className="header-avatar-button"
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            <Avatar 
              src={selectedStudent?.photo}
              className="header-avatar"
            >
              {selectedStudent?.name?.charAt(0) || 'S'}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={goTo('/profile')}>
              <Person className="header-menu-icon" />
              Profile
            </MenuItem>
            <MenuItem onClick={goTo('/progress')}>
              <InsightsOutlined className="header-menu-icon" />
              My Progress
            </MenuItem>
            <MenuItem onClick={goTo('/practice')}>
              <Psychology className="header-menu-icon" />
              Practice
            </MenuItem>
            <Divider />
            {hasMultipleStudents && (
              <MenuItem onClick={handleSwitchStudent}>
                <SwitchAccount className="header-menu-icon" />
                Switch Student
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout} className="header-logout-item">
              Logout
            </MenuItem>
          </Menu>
        </Stack>
      </Box>

      {/* Authentication Error Snackbar */}
      <Snackbar
        open={showAuthError}
        autoHideDuration={10000}
        onClose={handleAuthErrorClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAuthErrorClose}
          severity="warning"
          icon={<Warning />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleAuthErrorLogout}
              sx={{ color: 'white', fontWeight: 'bold' }}
            >
              Logout
            </Button>
          }
          sx={{
            width: '100%',
            maxWidth: '400px',
            '& .MuiAlert-message': {
              flex: 1,
            }
          }}
        >
          {authErrorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header;